/* eslint-disable complexity */
import { Box, Grid, Chip, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import { flagImgs, flagOptionList } from '@components/FlagPopup/constants';
import {
  CheckCircleOutlineIcon,
  CloseIcon,
  InformationIcon,
  AlertIcon,
  AlertOutlineIcon,
} from '@atgfr8/icons';
import { FlagType } from '@typeDefinitions/flagTypes';
import { isEmpty } from 'lodash';
import { JSX, Show, createSignal, createUniqueId, mergeProps } from 'solid-js';

import cls, { colors, notificationStyles } from './classes';

export type typeOptions =
  | 'warning'
  | 'info'
  | 'error'
  | 'Red Flag'
  | 'Green Flag'
  | 'Blue Flag'
  | 'Yellow Flag'
  | 'Orange Flag'
  | 'Needs Approval'
  | 'Get Paperwork'
  | 'Refund Request'
  | 'Efs Issued'
  | 'Missing Paperwork'
  | 'Hot Load'
  | 'Billing Hold'
  | 'Billing Note'
  | 'tableError'
  | 'Tender Updated'
  | 'success'
  | 'AI Escalated'
  | 'Needs Review'
  | '';

interface NotificationProps {
  startIcon?: string;
  type: typeOptions | FlagType | string;
  text?: JSX.Element | string;
  subText?: JSX.Element | string;
  endIcon?: JSX.Element;
  playIcon?: JSX.Element;
  accordion?: boolean | undefined;
  expanded?: boolean;
  cleared?: boolean;
  disputed?: boolean;
  onToggle?: () => void;
  onClick?: () => void;
  tableRowNotification?: boolean;
  sxProps?: string;
  imgIcon?: string;
  getCustomStyle?: () => string | undefined;
  needsApprovalFlag?: boolean;
  commentLength?: JSX.Element | string;
  onClose?: () => void;
  isNotFlagTitle?: boolean;
  typeIconStyleContainerClasses?: string;
  gapStyle?: string;
  style?: JSX.CSSProperties;
}

const defaultProps = {
  accordion: false,
  expanded: false,
  tableRowNotification: false,
};

const iconMap = (type: string): JSX.Element => {
  switch (type) {
    case 'warning':
      return (
        <AlertIcon style={{ 'font-size': '24px', color: colors.warning }} />
      );
    case 'success':
      return (
        <CheckCircleOutlineIcon
          style={{ 'font-size': '24px', color: colors.success }}
        />
      );
    case 'error':
    case 'tableError':
      return (
        <AlertOutlineIcon
          style={{ 'font-size': '24px', color: colors.error }}
        />
      );
    case 'info':
      return (
        <InformationIcon style={{ 'font-size': '24px', color: colors.info }} />
      );
    case flagOptionList[type]:
      return <img src={flagImgs[type]} class="p-[12px]" alt="alt flag" />;
    default:
      return (
        <InformationIcon style={{ 'font-size': '24px', color: colors.info }} />
      );
  }
};

export const conditionalCursor = (condition: boolean) => ({
  cursor: condition ? 'pointer' : 'default',
});

const Notification = (props: NotificationProps): JSX.Element => {
  const uid = createUniqueId();
  const mp = mergeProps(defaultProps, props);
  const [isExpanded, setIsExpanded] = createSignal(
    mp.accordion ? mp.expanded : false,
  );
  const handleToggle = () => {
    setIsExpanded(!isExpanded());
    mp.onToggle && mp.onToggle();
  };

  return (
    <Stack
      spacing={mp.gapStyle ?? 3}
      alignItems="center"
      direction="row"
      style={props.style}
      class={`${cls.container} ${mp.sxProps} ${
        mp.tableRowNotification && 'my-0!'
      } ${
        mp.getCustomStyle
          ? mp.getCustomStyle()
          : !isEmpty(flagOptionList[mp.type])
            ? cls[flagOptionList[mp.type]]
            : cls[mp.type]
      } flex-row [&:First-child]:mb-0`}
      sx={{
        'box-shadow': mp.tableRowNotification
          ? ''
          : '0px 1px 18px 0px rgba(0, 0, 0, 0.12), 0px 6px 10px 0px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20);',
        opacity: Boolean(props.cleared) ? 0.6 : 1,
      }}
      onClick={mp.onClick}
    >
      <Box class={`${mp.typeIconStyleContainerClasses} w-auto`}>
        {Boolean(mp.imgIcon) ? (
          <img src={mp.imgIcon} class="size-[44px]" alt="alt flag" />
        ) : (
          iconMap(mp.type)
        )}
      </Box>
      <Box class="flex-1 relative">
        <Stack direction="column">
          <Box width="100%">
            <Grid item size={{ xs: 12, md: 'auto' }}>
              <Stack direction="row" flexWrap="nowrap" alignContent="flex-end">
                <Stack direction="column" flex={1}>
                  <Stack direction="row">
                    <div
                      class={
                        (props.isNotFlagTitle ?? false) ? '' : cls.flagTitleText
                      }
                    >
                      {mp.text}
                    </div>
                    {(props.disputed ?? false) ? (
                      <Chip
                        id={`disputed-chip-${uid}`}
                        text="Disputed"
                        class="ml-2 bg-[#ed6c02]! text-white! text-xs!"
                      />
                    ) : undefined}
                  </Stack>
                  <div class="break-all">
                    {mp.accordion
                      ? typeof mp.subText === 'string' &&
                        mp.subText.length > 360
                        ? isExpanded()
                          ? mp.subText
                          : mp.subText.substring(0, 360) + '...'
                        : mp.subText
                      : mp.subText}
                  </div>
                </Stack>
                {mp.commentLength !== undefined && (
                  <Grid
                    item
                    size={{ xs: 1, md: 'auto' }}
                    class={cls.gridFlagComments}
                  >
                    <div>{mp.commentLength}</div>
                  </Grid>
                )}
                {props.onClose && (
                  <Grid
                    item
                    size={{ xs: 1, md: 'auto' }}
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                  >
                    <CloseIcon
                      onClick={props.onClose}
                      class="text-gray-400 cursor-pointer hover:text-gray-500 mr-2"
                    />
                  </Grid>
                )}
              </Stack>
            </Grid>
          </Box>
          <Show
            when={
              mp.accordion &&
              typeof mp.subText === 'string' &&
              mp.subText.length > 360
            }
          >
            <Box class="pt-2!">
              <Typography
                variant="body2"
                component="span"
                sxProps={{
                  ...notificationStyles.alignRight,
                  ...conditionalCursor(mp.subText !== ''),
                }}
                onClick={mp.accordion ? handleToggle : undefined}
              >
                {isExpanded() ? mp.endIcon : mp.playIcon}
              </Typography>
            </Box>
          </Show>
        </Stack>
      </Box>
    </Stack>
  );
};

export default Notification;
